import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFilter } from '../context/FilterContext';
import { useTranslation } from '../../i18n/hooks';
import './BookmakerFilter.css';

/**
 * BookmakerFilter - Checkbox-based multi-select interface for bookmaker filtering
 * Based on Listed_surebets.html mockup (lines 44-100)
 * 
 * Features:
 * - Grid layout with checkboxes for ~18 bookmakers
 * - Select all/deselect all toggle button
 * - Search/filter functionality for bookmaker names
 * - Selected count indicator
 * - Active filter chips display
 * - Loading states
 * - Responsive mobile design
 * - Inactive mode for non-subscribed users (informative only)
 * 
 * @param {boolean} inactive - If true, shows filter in read-only mode for informative purposes
 * @param {boolean} showSearch - Show search input for filtering bookmaker names
 *   (disabled in live FilterPanel — all licensed operators are listed)
 * @param {boolean} showSelectedChips - Show selected bookmakers as chips
 * @param {string} className - Additional CSS classes
 */
const BookmakerFilter = ({ 
  inactive = false,
  showSearch = false,
  showSelectedChips = true,
  className = '' 
}) => {
  const { t } = useTranslation();
  const {
    selectedBookmakers,
    availableBookmakers,
    selectedBookmakerCount,
    totalBookmakerCount,
    toggleBookmaker,
    selectAllBookmakers,
    deselectAllBookmakers,
  } = useFilter();

  const [searchQuery, setSearchQuery] = useState('');

  /**
   * Filter bookmakers by search query
   */
  const filteredBookmakers = useMemo(() => {
    if (!searchQuery.trim()) {
      return availableBookmakers;
    }

    const query = searchQuery.toLowerCase();
    return availableBookmakers.filter(bookmaker =>
      bookmaker.name.toLowerCase().includes(query)
    );
  }, [availableBookmakers, searchQuery]);

  /**
   * Check if all bookmakers are selected
   */
  const isAllSelected = selectedBookmakerCount === totalBookmakerCount;

  /**
   * Toggle all bookmakers (select all / deselect all)
   */
  const handleToggleAll = () => {
    if (inactive) return; // Prevent action in inactive mode
    
    if (isAllSelected) {
      deselectAllBookmakers();
    } else {
      selectAllBookmakers();
    }
  };

  /**
   * Handle bookmaker checkbox change
   */
  const handleBookmakerToggle = (bookmakerId) => {
    if (inactive) return; // Prevent action in inactive mode
    
    toggleBookmaker(bookmakerId);
  };

  /**
   * Handle search input change
   */
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  /**
   * Clear search query
   */
  const handleClearSearch = () => {
    setSearchQuery('');
  };

  return (
    <div className={`bookmaker-filter ${inactive ? 'bookmaker-filter--inactive' : ''} ${className}`}>
      {/* Toggle All Button */}
      <div className="bookmaker-filter__header">
        <button
          onClick={handleToggleAll}
          disabled={inactive}
          className={`bookmaker-filter__toggle-all ${isAllSelected ? 'bookmaker-filter__toggle-all--active' : ''}`}
          aria-label={isAllSelected ? t('filters.deselectAll') : t('filters.selectAll')}
        >
          {t('filters.all')}
          {!inactive && (
            <span className="bookmaker-filter__count">
              ({selectedBookmakerCount}/{totalBookmakerCount})
            </span>
          )}
        </button>

        {inactive && (
          <div className="bookmaker-filter__inactive-badge">
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 16 16" 
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M8 1C4.134 1 1 4.134 1 8s3.134 7 7 7 7-3.134 7-7-3.134-7-7-7zm0 12.5c-3.033 0-5.5-2.467-5.5-5.5S4.967 2.5 8 2.5s5.5 2.467 5.5 5.5-2.467 5.5-5.5 5.5z"/>
              <path d="M7.25 5.5h1.5v4h-1.5zm0 5h1.5v1.5h-1.5z"/>
            </svg>
            <span>{t('filters.inactiveMode')}</span>
          </div>
        )}
      </div>

      {/* Search Input */}
      {showSearch && (
        <div className="bookmaker-filter__search">
          <div className="bookmaker-filter__search-input-wrapper">
            <svg 
              className="bookmaker-filter__search-icon"
              width="16" 
              height="16" 
              viewBox="0 0 16 16" 
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
            </svg>
            <input
              type="text"
              className="bookmaker-filter__search-input"
              placeholder={t('filters.searchBookmakers')}
              value={searchQuery}
              onChange={handleSearchChange}
              disabled={inactive}
              aria-label={t('filters.searchBookmakers')}
            />
            {searchQuery && (
              <button
                className="bookmaker-filter__search-clear"
                onClick={handleClearSearch}
                aria-label={t('filters.clearSearch')}
                disabled={inactive}
              >
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 16 16" 
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                </svg>
              </button>
            )}
          </div>
          {searchQuery && filteredBookmakers.length === 0 && (
            <p className="bookmaker-filter__no-results">
              {t('filters.noBookmakersFound')}
            </p>
          )}
        </div>
      )}

      {/* Bookmaker Checkboxes Panel */}
      <div className="bookmaker-filter__panel">
        <h3 className="bookmaker-filter__panel-title">
          {t('filters.filterByBookmaker')}
        </h3>
        <div className="bookmaker-filter__grid">
          {filteredBookmakers.map((bookmaker) => (
            <label
              key={bookmaker.id}
              className={`bookmaker-filter__item ${
                selectedBookmakers.includes(bookmaker.id) 
                  ? 'bookmaker-filter__item--selected' 
                  : ''
              } ${inactive ? 'bookmaker-filter__item--disabled' : ''}`}
            >
              <input
                type="checkbox"
                className="bookmaker-filter__checkbox"
                checked={selectedBookmakers.includes(bookmaker.id)}
                onChange={() => handleBookmakerToggle(bookmaker.id)}
                disabled={inactive}
                value={bookmaker.id}
                aria-label={bookmaker.name}
              />
              <span className="bookmaker-filter__name">{bookmaker.name}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Selected Bookmakers Chips */}
      {showSelectedChips && selectedBookmakers.length > 0 && (
        <div className="bookmaker-filter__chips">
          <div className="bookmaker-filter__chips-label">
            {t('filters.selectedBookmakers')}:
          </div>
          <div className="bookmaker-filter__chips-container">
            {selectedBookmakers
              .map((id) => availableBookmakers.find((b) => b.id === id))
              .filter(Boolean)
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((bookmaker) => (
                <span
                  key={bookmaker.id}
                  className="bookmaker-filter__chip"
                  title={bookmaker.name}
                >
                  {bookmaker.name}
                </span>
              ))}
          </div>
        </div>
      )}

      {/* Inactive Mode Message */}
      {inactive && (
        <div className="bookmaker-filter__inactive-message">
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 16 16" 
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 12.5a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11z"/>
            <path d="M8 4.5a.5.5 0 0 1 .5.5v3a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5zm0 6a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5z"/>
          </svg>
          <p>{t('filters.inactiveMessage')}</p>
        </div>
      )}
    </div>
  );
};

BookmakerFilter.propTypes = {
  inactive: PropTypes.bool,
  showSearch: PropTypes.bool,
  showSelectedChips: PropTypes.bool,
  className: PropTypes.string,
};

export default BookmakerFilter;

